import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Divider,
  Paper,
  Typography,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
} from '@mui/material';
import { useGoogle_Login } from '../hooks/useGoogle_Login';
import { useUnityBuildStore } from '../stores/store';
import { GetUserTokenRequest } from '../api/protosCompiled/userToken/userToken_pb';
import { clients } from '../clients/grpcClients';
import { useLocalStorage } from '../stores/localStorage';
import { resetAuthFailedState } from '../hooks/useStopSyncOnAuthError';
import { Helmet } from 'react-helmet-async';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { rarityColors, amberColors } from '../theme/themeConfig';

// Import Material UI Icons
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import WarningIcon from '@mui/icons-material/Warning';

export const LogIn: React.FC = () => {
  const theme = useTheme(); // Access the current theme
  const { darkMode, toggleDarkMode } = useCustomTheme();
  const googleLogin = useGoogle_Login();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const setIsFetchingToken = useLocalStorage().setIsFetchingToken;
  const setSessionToken = useLocalStorage().setSessionToken;
  const setIsLoggedIn = useUnityBuildStore().setIsLoggedIn;
  const setProjectListIsMounted = useUnityBuildStore().setProjectListIsMounted;

  // State for manual login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Check for existing session token on component mount
  React.useEffect(() => {
    const existingToken = localStorage.getItem('sessionToken');
    const pendingSubscription = localStorage.getItem('pendingSubscription') === 'true';
    if (existingToken && existingToken !== '' && !pendingSubscription) {
      // Try to use existing token
      setSessionToken(existingToken);
      setIsLoggedIn(true);
      setProjectListIsMounted(true);
    }
  }, [setSessionToken, setIsLoggedIn, setProjectListIsMounted]);

  const handleManualLogin = async () => {
    // Reset auth failed state since we're trying to login
    resetAuthFailedState();

    setIsFetchingToken(true);
    setError(null);
    try {
      // Create a new request object
      const request = new GetUserTokenRequest();
      request.setEmail(email);
      request.setPassword(password);

      // Make the gRPC call
      const response = await clients.userTokenServiceClient.getToken(request);

      const responseError = response.getError ? response.getError() : undefined;

      // Special handling: backend returns a token but flags no active subscription
      if (responseError === 'subscription_required' || responseError === 'no subscription found') {
        const token =
          typeof response.getSessionToken === 'function' ? response.getSessionToken() : '';
        if (token) {
          setSessionToken(token);
          // Mark that we're mid-subscription flow so the login auto-login on mount doesn't flip back
          localStorage.setItem('pendingSubscription', 'true');
        }
        setError(responseError);
        setIsLoggedIn(false);
        setProjectListIsMounted(false);
        return;
      }

      if (response && response.getSessionToken() && !responseError) {
        // Successful login
        setSessionToken(response.getSessionToken());
        setIsLoggedIn(true);
        setProjectListIsMounted(true);

        // Make sure to reset any auth failed state after successful login
        resetAuthFailedState();
      } else {
        // Handle login failure
        if (responseError) {
          setError(responseError); // Explicitly set the error from the response
        } else {
          setError('Invalid email or password.'); // Fallback error message
        }
        setIsLoggedIn(false);
        setProjectListIsMounted(false);
      }
    } catch (err: any) {
      console.error('Error during manual login:', err);
      setError(err.message || 'An error occurred during login. Please try again.');
    } finally {
      setIsFetchingToken(false);
    }
  };

  // Sidebar content
  const sidebarContent = (
    <Box sx={{ width: 256, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: darkMode ? '#ffffff' : amberColors[600] }}
          >
            Unity Build Estimation
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            size="small"
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Chip
          label="Beta"
          size="small"
          sx={{
            bgcolor: rarityColors.epic,
            color: 'white',
            fontWeight: 600,
          }}
        />
      </Box>

      {/* Theme Toggle */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={toggleDarkMode} size="small">
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          <Typography variant="body2" sx={{ color: darkMode ? '#ffffff' : 'text.secondary' }}>
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, p: 2 }}>
        <Typography
          variant="overline"
          sx={{
            color: darkMode ? '#ffffff' : 'text.secondary',
            fontWeight: 600,
            mb: 2,
            display: 'block',
          }}
        >
          NAVIGATION
        </Typography>
        <List sx={{ p: 0 }}>
          <ListItem
            button
            component="a"
            href="/"
            sx={{
              borderRadius: 1,
              mb: 1,
              '&:hover': {
                bgcolor: alpha(amberColors[600], 0.15),
                '& .MuiListItemText-primary': {
                  color: darkMode ? '#ffffff' : 'inherit',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#ffffff' : amberColors[600],
                },
              },
            }}
          >
            <ListItemIcon>
              <HomeIcon sx={{ color: darkMode ? '#ffffff' : 'inherit' }} />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              sx={{ '& .MuiTypography-root': { color: darkMode ? '#ffffff' : 'inherit' } }}
            />
          </ListItem>
          <ListItem
            sx={{
              borderRadius: 1,
              mb: 1,
              cursor: 'not-allowed',
              opacity: 0.6,
            }}
          >
            <ListItemIcon>
              <BusinessIcon sx={{ color: darkMode ? '#ffffff' : 'inherit' }} />
            </ListItemIcon>
            <ListItemText
              primary="The Foundry"
              sx={{ '& .MuiTypography-root': { color: darkMode ? '#ffffff' : 'inherit' } }}
            />
            <Chip
              label="Coming Soon!"
              size="small"
              sx={{
                bgcolor: rarityColors.rare,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
              }}
            />
          </ListItem>
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="caption"
          sx={{ color: darkMode ? '#ffffff' : 'text.secondary', display: 'block', mb: 1 }}
        >
          © 2025 Unity Build Estimation
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <WarningIcon sx={{ fontSize: 16, color: rarityColors.epic, mr: 0.5 }} />
          <Typography variant="caption" sx={{ color: rarityColors.epic }}>
            beta development
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Sign In - Unity Build</title>
        <meta
          name="description"
          content="Sign in to your Unity Build account to access powerful construction estimation tools."
        />
      </Helmet>

      {/* Mobile Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 256,
            bgcolor: darkMode ? 'background.paper' : 'background.default',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main Layout with Blueprint Background */}
      <Box
        sx={{
          minHeight: '100vh',
          ...(darkMode
            ? {
                background: `
                  linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
                  url('/desktop_blueprint.webp')
                `,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: { xs: 'scroll', md: 'fixed' },
              }
            : {
                background: `
                  linear-gradient(135deg, ${alpha('#f9fafb', 0.78)} 0%, ${alpha('#f3f4f6', 0.78)} 50%, ${alpha('#f9fafb', 0.78)} 100%),
                  url('/desktop_blueprint.webp')
                `,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: { xs: 'scroll', md: 'fixed' },
              }),
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Mobile Menu Button */}
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1000,
            display: { xs: 'block', md: 'none' },
          }}
        >
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              bgcolor: alpha(darkMode ? '#1a1a1a' : '#ffffff', 0.9),
              backdropFilter: 'blur(10px)',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                bgcolor: alpha(darkMode ? '#2d2d2d' : '#f5f5f5', 0.9),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop Sidebar */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: 256,
            zIndex: 900,
            display: { xs: 'none', md: 'block' },
            bgcolor: darkMode ? 'background.paper' : 'background.default',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {sidebarContent}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            ml: 0,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 6,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper
              elevation={3}
              sx={{
                maxWidth: 400,
                width: '100%',
                padding: 3,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[3],
              }}
            >
              {/* Title */}
              <Typography
                variant="h5"
                component="h1"
                fontWeight={600}
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Sign In
              </Typography>

              {/* Display error prominently at the top */}
              {error && (
                <Box
                  sx={{
                    color: theme.palette.error.main,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(229, 57, 53, 0.15)' // Dark mode error background
                        : 'rgba(239, 83, 80, 0.1)', // Light mode error background
                    padding: 2,
                    borderRadius: 1,
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  <Typography variant="body2">{error}</Typography>
                </Box>
              )}

              {(error === 'subscription_required' || error === 'no subscription found') && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    window.location.href = '/checkout';
                  }}
                  fullWidth
                  sx={{
                    marginBottom: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 0',
                    borderRadius: 1,
                  }}
                >
                  Create subscription
                </Button>
              )}

              {/* Login Options */}
              <Box component="div" sx={{ marginBottom: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={googleLogin}
                  fullWidth
                  sx={{
                    marginBottom: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 0',
                    borderRadius: 1,
                  }}
                >
                  Log in with Google
                </Button>
                <Divider
                  sx={{
                    marginY: 2,
                    color: theme.palette.text.secondary,
                    '&::before, &::after': {
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.12)'
                          : 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  OR
                </Divider>
              </Box>

              {/* Manual Login Form */}
              <Box component="form" noValidate autoComplete="off">
                <TextField
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  InputLabelProps={{
                    sx: { color: theme.palette.text.secondary },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      color:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.7)
                          : alpha(theme.palette.common.black, 0.6),
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.23)
                            : alpha(theme.palette.common.black, 0.23),
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  InputLabelProps={{
                    sx: { color: theme.palette.text.secondary },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      color:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.7)
                          : alpha(theme.palette.common.black, 0.6),
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.23)
                            : alpha(theme.palette.common.black, 0.23),
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleManualLogin}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    marginTop: 3,
                    padding: { xs: '8px 0', sm: '10px 0' },
                    borderRadius: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  Log In
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LogIn;
